import { z } from 'zod';
import { abilities, buildings, sectors, units } from './content';
import { factionSchema, unitSchema } from './types';

const pointSchema = z.object({ x: z.number().finite(), y: z.number().finite() });
const buildingSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  faction: factionSchema,
  name: z.string().min(1),
  cost: z.number().positive(),
  buildTime: z.number().positive(),
  prerequisites: z.array(z.string()),
  unlocks: z.array(z.string()),
});
const sectorSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  type: z.enum(['base', 'supply', 'victory', 'connector']),
  point: pointSchema,
  neighbors: z.array(z.string()),
  initialOwner: factionSchema.optional(),
});
const abilitySchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  source: z.string(),
  faction: factionSchema,
  supplyCost: z.number().nonnegative(),
  cooldown: z.number().positive(),
  kind: z.enum([
    'smoke',
    'damage',
    'recon',
    'shield',
    'land',
    'deploy',
    'heal',
    'scan',
    'headwind',
  ]),
  radius: z.number().positive(),
  damage: z.number().nonnegative().optional(),
  delay: z.number().nonnegative().optional(),
  minimumRange: z.number().nonnegative().optional(),
  directional: z.boolean().optional(),
  telegraphed: z.boolean().optional(),
  lockTime: z.number().nonnegative().optional(),
});

const unique = (ids: string[], label: string): void => {
  if (new Set(ids).size !== ids.length) throw new Error(`Duplicate ${label} ID.`);
};

/** Throws a readable startup error rather than accepting corrupt shipped content. */
export const validateShippedContent = (): void => {
  for (const definition of units) unitSchema.parse(definition);
  for (const definition of buildings) buildingSchema.parse(definition);
  for (const definition of sectors) sectorSchema.parse(definition);
  for (const definition of abilities) abilitySchema.parse(definition);
  unique(
    units.map((entry) => entry.id),
    'unit',
  );
  unique(
    buildings.map((entry) => entry.id),
    'building',
  );
  unique(
    sectors.map((entry) => entry.id),
    'sector',
  );
  unique(
    abilities.map((entry) => entry.id),
    'ability',
  );
  const sectorIds = new Set(sectors.map((entry) => entry.id));
  for (const sector of sectors)
    for (const neighbor of sector.neighbors) {
      if (!sectorIds.has(neighbor))
        throw new Error(`Sector ${sector.id} references unknown neighbor ${neighbor}.`);
      if (!sectors.find((entry) => entry.id === neighbor)?.neighbors.includes(sector.id))
        throw new Error(`Sector link ${sector.id} → ${neighbor} is not reciprocal.`);
    }
  for (const faction of ['equestria', 'interdef'] as const) {
    if (
      sectors.filter((entry) => entry.type === 'base' && entry.initialOwner === faction).length !==
      1
    )
      throw new Error(`Expected exactly one ${faction} base sector.`);
  }
  if (sectors.filter((entry) => entry.type === 'supply').length !== 7)
    throw new Error('Kharkiv Approaches must ship seven Supply Depots.');
  if (sectors.filter((entry) => entry.type === 'victory').length !== 3)
    throw new Error('Kharkiv Approaches must ship three Victory Points.');
  for (const building of buildings)
    for (const unitId of building.unlocks)
      if (!units.some((entry) => entry.id === unitId))
        throw new Error(`Building ${building.id} unlocks unknown unit ${unitId}.`);
  for (const unit of units) {
    const source = buildings.find((entry) => entry.id === unit.source);
    if (!source) throw new Error(`Unit ${unit.id} references unknown reinforcement source.`);
    if (source.faction !== unit.faction)
      throw new Error(`Unit ${unit.id} has a cross-faction reinforcement source.`);
    if (!source.unlocks.includes(unit.id))
      throw new Error(`Unit ${unit.id} is not requestable from ${source.id}.`);
  }
  for (const ability of abilities) {
    const source = buildings.find((entry) => entry.id === ability.source);
    if (!source) throw new Error(`Ability ${ability.id} references unknown support source.`);
    if (source.faction !== ability.faction)
      throw new Error(`Ability ${ability.id} has a cross-faction support source.`);
  }
};
